import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MyContext } from "../MainContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheck, faPaperPlane, faTimes, faClock } from '@fortawesome/free-solid-svg-icons';
import { Modal, Button } from 'react-bootstrap';
import { backendUrl } from '../utils/variables';


export default function SendMessageRequest({ profileUserId, isFollowing, profileUsername }) {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const [requestStatus, setRequestStatus] = useState(null); // 'pending', 'accepted', 'rejected', or null
    const [showModal, setShowModal] = useState(false);
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [message, setMessage] = useState('');

    const API_BASE = backendUrl;


    // Check message request status when component loads or when profileUserId changes
    useEffect(() => {
        if (user && user.user_id && profileUserId && isFollowing) {
            checkRequestStatus();
        }
    }, [user, profileUserId, isFollowing]);

    const checkRequestStatus = async () => {
        if (!user || !user.user_id || !profileUserId) return;

        setCheckingStatus(true);
        try {
            const response = await fetch(`${API_BASE}/check_message_request_status.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sender_id: user.user_id,
                    receiver_id: profileUserId
                }),
            });

            const data = await response.json();
            if (data.success) {
                if (data.exists) {
                    setRequestStatus(data.status);
                    if (data.status === 'accepted') {
                        setRequestSent(true);
                    } else if (data.status === 'pending') {
                        setRequestSent(true);
                    }
                } else {
                    setRequestStatus(null);
                    setRequestSent(false);
                }
            }
        } catch (error) {
            console.error('Error checking request status:', error);
        } finally {
            setCheckingStatus(false);
        }
    };

    const handleSendRequest = async () => {
        if (!user || !user.user_id || !profileUserId || loading) return;

        if (!isFollowing) {
            alert('You must follow this user before sending a message');
            return;
        }

        if (!message.trim()) {
            alert('Please enter a message');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE}/send_message_request.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sender_id: user.user_id,
                    receiver_id: profileUserId,
                    message: message.trim()
                }),
            });

            const data = await response.json();
            if (data.success) {
                setShowModal(false);
                setMessage('');
                // Refresh status check to get latest status
                await checkRequestStatus();
                // If already accepted, navigate to chat
                if (data.messages && data.messages.includes('already accepted')) {
                    navigate(`/messages/${profileUserId}`);
                }
            } else {
                alert(data.messages || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleMessageButtonClick = async () => {
        // Refresh status check first and wait for result
        setCheckingStatus(true);
        try {
            const response = await fetch(`${API_BASE}/check_message_request_status.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sender_id: user.user_id,
                    receiver_id: profileUserId
                }),
            });

            const data = await response.json();
            let currentStatus = requestStatus;
            
            if (data.success && data.exists) {
                currentStatus = data.status;
                setRequestStatus(data.status);
            } else {
                currentStatus = null;
                setRequestStatus(null);
            }
            
            // Then handle based on status
            if (currentStatus === 'accepted') {
                // Navigate to chat if already accepted
                navigate(`/messages/${profileUserId}`);
            } else if (currentStatus === 'pending') {
                // Show custom modal that request is pending
                setShowPendingModal(true);
            } else {
                // No request exists, show modal to send first message
                setShowModal(true);
            }
        } catch (error) {
            console.error('Error checking request status:', error);
            // On error, just show modal
            setShowModal(true);
        } finally {
            setCheckingStatus(false);
        }
    };

    const handleOpenChat = () => {
        navigate(`/messages/${profileUserId}`);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendRequest();
        }
    };

    if (!user || !user.user_id || user.user_id == profileUserId) {
        return null;
    }

    // Render button based on status
    let buttonElement;
    if (checkingStatus) {
        buttonElement = (
            <button className="btn btn-secondary" disabled>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Checking...
            </button>
        );
    } else if (requestStatus === 'accepted') {
        buttonElement = (
            <button className="btn btn-success" onClick={handleOpenChat}>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Message
            </button>
        );
    } else if (requestStatus === 'pending') {
        buttonElement = (
            <button className="btn btn-warning" onClick={handleMessageButtonClick}>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Pending
            </button>
        );
    } else {
        buttonElement = (
            <button 
                className="btn btn-primary" 
                onClick={handleMessageButtonClick}
                disabled={!isFollowing}
            >
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Message
            </button>
        );
    }

    return (
        <>
            {buttonElement}

            {/* Send Message Modal */}
            <Modal show={showModal} onHide={() => { setShowModal(false); setMessage(''); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Send Message</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <textarea
                        className="form-control"
                        rows="4"
                        placeholder="Type your message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        style={{ resize: 'none' }}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowModal(false); setMessage(''); }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSendRequest}
                        disabled={loading || !message.trim()}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                        {loading ? 'Sending...' : 'Send'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Pending Request Modal */}
            <Modal show={showPendingModal} onHide={() => setShowPendingModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: '#ffc107' }} />
                        Message Request Pending
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-3">
                        <FontAwesomeIcon 
                            icon={faEnvelope} 
                            size="3x" 
                            className="mb-3" 
                            style={{ color: '#ffc107', opacity: 0.7 }}
                        />
                        <p className="mb-2" style={{ fontSize: '1.1em' }}>
                            <strong>@{profileUsername || 'user'}</strong> is yet to accept your message request.
                        </p>
                        <p className="text-muted" style={{ fontSize: '0.9em' }}>
                            You'll be able to send messages once they accept your request.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPendingModal(false)}>
                        Close
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            setShowPendingModal(false);
                            navigate('/messages/requests');
                        }}
                        style={{ backgroundColor: '#48887b', borderColor: '#48887b' }}
                    >
                        View Requests
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

